import { extractUnitNumber, formatState } from '../system/location';

export interface AutocompleteResult {
  label: string;
  language: string;
  countryCode: string;
  locationId: string;
  address: {
    country: string;
    state: string;
    county: string;
    city: string;
    district?: string;
    street: string;
    postalCode: string;
    houseNumber?: string;
    unitNumber?: string;
  };
  matchLevel: string;
}

interface AutocompleteResponse {
  suggestions: AutocompleteResult[];
}

export interface GeocodedLocation {
  locationId: string;
  locationType: string;
  displayPosition: {
    Latitude: number;
    Longitude: number;
  };
  navigationPosition: Array<{
    latitude: number;
    longitude: number;
  }>;
  mapView: {
    topLeft: {
      latitude: number;
      longitude: number;
    };
    bottomRight: {
      latitude: number;
      longitude: number;
    };
  };
  address: {
    label: string;
    country: string;
    state: string;
    county: string;
    city: string;
    district: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    unitNumber?: string;
    additionalData: Array<{
      value: string;
      key: string;
    }>;
  };
}

export interface GeocodingResult {
  label: string;
  countryCode: string;
  countryName: string;
  stateCode: string;
  state: string;
  countyCode: string;
  county: string;
  city: string;
  district: string;
  street: string;
  postalCode: string;
  houseNumber: string;
  locationId: string;
  locationType: string;
  latitude: number;
  longitude: number;
}

export const searchAddressHere = async (
  apiKey: string,
  query: string,
  proximityCenter: string,
): Promise<AutocompleteResult[]> => {
  const { address: cleanAddress, unitNumber } = extractUnitNumber(query);
  const url = `https://autocomplete.search.hereapi.com/v1/autocomplete?q=${encodeURIComponent(cleanAddress)}&in=countryCode:USA&at=${proximityCenter}&limit=5&apiKey=${apiKey}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    return data.items.map((item: any) => ({
      label: item.address.label,
      language: item.language,
      countryCode: item.address.countryCode,
      locationId: item.id,
      address: {
        country: item.address.countryName,
        state: formatState(item.address.state),
        county: item.address.county,
        city: item.address.city,
        district: item.address.district,
        street: item.address.street,
        postalCode: item.address.postalCode,
        houseNumber: item.address.houseNumber,
        unitNumber: unitNumber,
      },
      matchLevel: item.resultType,
    }));
  } catch (error) {
    console.error('Error fetching autocomplete results:', error);
    return [];
  }
};

export const geocodeLocationId = async (
  apiKey: string,
  locationId: string,
): Promise<GeocodedLocation | undefined> => {
  const url = `https://lookup.search.hereapi.com/v1/lookup?id=${locationId}&apiKey=${apiKey}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();

    const { address: cleanAddress, unitNumber } = extractUnitNumber(
      result.address.label,
    );

    return {
      locationId: result.id,
      locationType: result.resultType,
      displayPosition: {
        Latitude: result.position.lat,
        Longitude: result.position.lng,
      },
      navigationPosition: [
        {
          latitude: result.position.lat,
          longitude: result.position.lng,
        },
      ],
      mapView: {
        topLeft: {
          latitude: result.mapView.north,
          longitude: result.mapView.west,
        },
        bottomRight: {
          latitude: result.mapView.south,
          longitude: result.mapView.east,
        },
      },
      address: {
        label: cleanAddress,
        country: result.address.countryName,
        state: formatState(result.address.state),
        county: result.address.county,
        city: result.address.city,
        district: result.address.district,
        street: result.address.street,
        houseNumber: result.address.houseNumber,
        postalCode: result.address.postalCode,
        unitNumber: unitNumber,
        additionalData: result.address.additionalData || [],
      },
    };
  } catch (error) {
    console.error('Error geocoding location ID:', error);
    return undefined;
  }
};

export const reverseGeocode = async (
  apiKey: string,
  lat: number,
  lng: number,
): Promise<any | null> => {
  const url = `https://revgeocode.search.hereapi.com/v1/revgeocode?at=${lat},${lng}&lang=en-US&apiKey=${apiKey}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    if (data.items && data.items.length > 0) {
      const item = data.items[0];
      return {
        label: item.address.label,
        language: 'en',
        countryCode: item.address.countryCode,
        locationId: item.id,
        address: {
          country: item.address.countryName,
          state: formatState(item.address.state),
          county: item.address.county,
          city: item.address.city,
          district: item.address.district,
          street: item.address.street,
          postalCode: item.address.postalCode,
          houseNumber: item.address.houseNumber,
        },
        matchLevel: item.resultType,
        geocodedLocation: {
          locationId: item.id,
          locationType: item.resultType,
          displayPosition: {
            Latitude: item.position.lat,
            Longitude: item.position.lng,
          },
        },
      };
    }
    return null;
  } catch (error) {
    console.error('Error in reverse geocoding:', error);
    return null;
  }
};

export const geocodePartialAddress = async (
  apiKey: string,
  partialAddress: {
    street?: string;
    city?: string;
    county?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  },
): Promise<GeocodingResult | null> => {
  const queryParams = new URLSearchParams();
  queryParams.append('apiKey', apiKey);

  // Build the query string
  const queryParts: string[] = [];
  if (partialAddress.street) queryParts.push(partialAddress.street);
  if (partialAddress.city) queryParts.push(partialAddress.city);
  if (partialAddress.state) queryParts.push(partialAddress.state);
  if (partialAddress.country) queryParts.push(partialAddress.country);

  queryParams.append('q', queryParts.join(', '));

  if (partialAddress.postalCode) {
    queryParams.append('postalCode', partialAddress.postalCode);
  }

  const url = `https://geocode.search.hereapi.com/v1/geocode?${queryParams.toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    if (data.items && data.items.length > 0) {
      const item = data.items[0];
      return {
        label: item.address.label,
        countryCode: item.address.countryCode,
        countryName: item.address.countryName,
        stateCode: item.address.stateCode,
        state: formatState(item.address.state),
        countyCode: item.address.countyCode,
        county: item.address.county,
        city: item.address.city,
        district: item.address.district,
        street: item.address.street,
        postalCode: item.address.postalCode,
        houseNumber: item.address.houseNumber,
        locationId: item.id,
        locationType: item.resultType,
        latitude: item.position.lat,
        longitude: item.position.lng,
      };
    }
    return null;
  } catch (error) {
    console.error('Error in geocoding:', error);
    return null;
  }
};
